import { motion } from 'framer-motion';
import { CheckCircle2, Users, Building2, Rocket } from 'lucide-react';

const phases = [
  { icon: CheckCircle2, phase: 'Phase 1', name: 'Validation', desc: 'Comprehensive resource data collection, independent technical review, and institutional positioning package development.' },
  { icon: Users, phase: 'Phase 2', name: 'Partnerships', desc: 'Strategic partner identification, due diligence alignment, and industrial collaboration framework establishment.' },
  { icon: Building2, phase: 'Phase 3', name: 'Infrastructure', desc: 'Brand architecture build-out, token framework engineering, and complete project structuring for market readiness.' },
  { icon: Rocket, phase: 'Phase 4', name: 'Expansion', desc: 'Global investor outreach campaigns, secondary market preparation, and operational scale-up across target geographies.' },
];

const headerMotion = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.6 },
};

const EASE = [0.22, 1, 0.36, 1];

function RoadmapItem({ item, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ delay: index * 0.12, duration: 0.5, ease: EASE }}
      data-testid={`roadmap-phase-${index}`}
      className="relative pl-16 lg:pl-20"
    >
      <div className="absolute left-4 lg:left-6 top-2 flex h-5 w-5 items-center justify-center">
        <div className="h-3 w-3 rounded-full bg-[#3B82F6] ring-4 ring-[rgba(59,130,246,0.15)]" />
      </div>

      <div className="card-glow rounded-sm border border-[rgba(59,130,246,0.1)] bg-[#0A1628] p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(59,130,246,0.25)]">
        <div className="flex items-center gap-3 mb-3">
          <item.icon className="w-5 h-5 text-[#3B82F6]" strokeWidth={1.5} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#60A5FA]">{item.phase}</span>
        </div>
        <div className="text-2xl font-bold text-[#F1F5F9]">{item.name}</div>
        <p className="mt-2 leading-relaxed text-[#94A3B8]">{item.desc}</p>
      </div>
    </motion.div>
  );
}

export default function RoadmapSection() {
  return (
    <section id="roadmap" data-testid="roadmap-section" className="py-24 lg:py-32" style={{ background: '#060B1E' }}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.9fr,1.1fr] items-start">
          <motion.div {...headerMotion}>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#3B82F6]">Token Framework</div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#F1F5F9]">
              Digital reserve concept for modern investor communication.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-[#94A3B8]">
              A structured digital-asset narrative designed for positioning and ecosystem storytelling around iodine reserves, extraction technology, and future industrial scale.
            </p>
            <div className="mt-8 rounded-sm border border-[rgba(59,130,246,0.2)] bg-[rgba(59,130,246,0.06)] p-6">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#60A5FA]">Positioning Statement</div>
              <p className="mt-3 text-lg leading-relaxed text-[#F1F5F9] italic">
                "We are building digital infrastructure around iodine reserves &mdash; combining resource quality, extraction know-how, and strategic market positioning into a unified investment thesis."
              </p>
            </div>
          </motion.div>

          <div className="relative">
            <div className="absolute left-6 lg:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[rgba(59,130,246,0.3)] to-transparent" />
            <div className="space-y-8">
              {phases.map((item, i) => (
                <RoadmapItem key={item.phase} item={item} index={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
